import { LD } from 'src/app/constants/launch-darkly-flags';
import { getLDClient } from 'src/app/utils/ld-client-do-not-use-me';

import { RateLimitedError } from '../utils';

import type { RequestMiddleware } from 'graphql-request/dist/types';

// Rate limiting configuration
const RATE_LIMIT_CONFIG = {
  timeWindow: 10000, // 10 second window
  maxRequests: 20, // 20 requests per 10 seconds (2 per second average)
};

// Track requests for rate limiting (sliding window approach)
const requestMap = new Map<string, { timestamps: number[] }>();

// Test utility to reset the request map (only available in test environment)
// This is necessary because rate limiting requires shared state across requests
export const __resetRequestMap = (): void => {
  if (process.env.NODE_ENV === 'test') {
    requestMap.clear();
  }
};

export const isRateLimitingEnabled = (): boolean => {
  try {
    const ldClient = getLDClient();
    if (ldClient) {
      return ldClient.variation(LD.GQL_RATE_LIMITING, false);
    }

    return false;
  } catch {
    return false;
  }
};

// Create operation fingerprint for GraphQL requests
const createOperationFingerprint = (url: string, operationName?: string, query?: string): string =>
  JSON.stringify({
    url,
    operationName,
    query: query?.trim(), // Normalize whitespace
  });

// Parse GraphQL request body to extract query and operationName, ignoring variables
const parseGraphQLBody = (body: string) => {
  try {
    const bodyData = JSON.parse(body);
    // bodyData.variables should be ignored for fingerprinting
    return {
      query: bodyData.query,
      operationName: bodyData.operationName,
    };
  } catch {
    return {
      query: body,
      operationName: undefined,
    };
  }
};

// GraphQLClient request middleware for rate limiting
export const createRateLimitMiddleware = (): RequestMiddleware => (request) => {
  const now = Date.now();

  // Parse the GraphQL request body to extract query and operationName
  const { query, operationName } =
    typeof request.body === 'string'
      ? parseGraphQLBody(request.body)
      : { query: undefined, operationName: request.operationName };

  if (query === undefined || operationName === undefined) {
    console.error('Invalid GraphQL request body', request.body);
    return request;
  }

  const fingerprint = createOperationFingerprint(request.url, operationName, query);

  // Get or create request record for this fingerprint
  let record = requestMap.get(fingerprint);
  if (!record) {
    record = { timestamps: [] };
    requestMap.set(fingerprint, record);
  }

  // Remove timestamps older than the time window
  const cutoffTime = now - RATE_LIMIT_CONFIG.timeWindow;
  record.timestamps = record.timestamps.filter((timestamp) => timestamp > cutoffTime);

  // Check if we've exceeded the rate limit
  if (record.timestamps.length >= RATE_LIMIT_CONFIG.maxRequests) {
    // Rate limit exceeded - block this request
    throw new RateLimitedError();
  }

  // Add this request timestamp and allow it through
  record.timestamps.push(now);
  return request;
};
